<template>
  <div class="flex items-center h-full w-full" :class="inline ? 'justify-start' : 'justify-center'">
    <div
      v-if="hasScore"
      class="flex items-center gap-0.5 relative"
      :class="inline ? 'flex-row gap-1.5' : 'flex-col'"
      @mouseenter="onBadgeEnter"
      @mouseleave="onBadgeLeave"
      ref="badge"
    >
      <!-- Main composite badge -->
      <div
        class="rounded-full px-2.5 py-0.5 text-sm font-semibold border bg-white min-w-[2.25rem] text-center"
        :class="badgeClass"
      >
        {{ roundedScore }}
      </div>

      <!-- Sub-metric indicator dots -->
      <div v-if="subMetricsList.length > 0" class="flex items-center gap-1">
        <div
          v-for="metric in subMetricsList"
          :key="metric.key"
          class="w-1.5 h-1.5 rounded-full"
          :class="metric.value != null ? getDotColorClass(metric.normalized) : 'border border-gray-300 bg-transparent'"
        />
      </div>

      <!-- Hover tooltip — compact metric list + button to open modal -->
      <Teleport to="body">
        <div
          v-if="tooltipVisible"
          class="fixed z-[9999]"
          :style="tooltipStyle"
          @mouseenter="onTooltipEnter"
          @mouseleave="onTooltipLeave"
        >
          <div class="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden w-[20rem]">
            <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
              <div class="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                Score Breakdown
              </div>
              <span class="text-sm font-bold text-gray-900 tabular-nums">{{ roundedScore }}</span>
            </div>

            <div class="px-3 py-2">
              <div
                v-for="(metric, idx) in subMetricsList"
                :key="'row-' + metric.key"
                :class="['py-1', idx > 0 ? 'border-t border-gray-100' : '']"
              >
                <div class="flex items-center gap-2 text-xs">
                  <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="getMetricColorClass(metric.normalized)" />
                  <span class="flex-1 truncate" :class="metric.value != null ? 'text-gray-800 font-medium' : 'text-gray-400'">
                    {{ metric.label }}
                  </span>
                  <span v-if="metric.weight" class="text-[10px] text-gray-500 tabular-nums">
                    {{ Math.round(metric.weight * 100) }}%
                  </span>
                  <span
                    class="font-semibold tabular-nums w-7 text-right"
                    :class="metric.value != null ? 'text-gray-900' : 'text-gray-400'"
                  >
                    {{ metric.display }}
                  </span>
                </div>
              </div>
            </div>

            <div class="px-3 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <span v-if="missingCount > 0" class="text-[10px] text-gray-500">
                {{ missingCount }} not yet available
              </span>
              <span v-else></span>
              <button
                type="button"
                class="text-[11px] font-medium text-primary-700 hover:text-primary-800 hover:underline pointer-events-auto"
                @click.stop="openModal"
              >
                More info →
              </button>
            </div>
          </div>
          <div class="flex justify-center">
            <div
              class="border-[6px] border-transparent border-t-white"
              style="filter: drop-shadow(0 1px 0 rgb(229 231 235));"
            />
          </div>
        </div>
      </Teleport>

      <!-- Full-screen modal with descriptions -->
      <Teleport to="body">
        <div
          v-if="modalOpen"
          class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-6 py-8"
          @click.self="closeModal"
        >
          <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-hidden flex flex-col">
            <!-- Modal header -->
            <div class="flex items-start justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 class="text-lg font-semibold text-gray-900">Engagement Score Breakdown</h3>
                <p class="text-xs text-gray-500 mt-0.5">
                  Composite of weighted sub-metrics. Hover values to see what's measured.
                </p>
              </div>
              <div class="flex items-center gap-3">
                <div
                  class="rounded-full px-3 py-0.5 text-base font-bold border bg-white"
                  :class="badgeClass"
                >
                  {{ roundedScore }}
                </div>
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-700 text-xl leading-none"
                  @click="closeModal"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            <!-- Modal body: per-metric cards -->
            <div class="px-6 py-4 overflow-y-auto flex-1 space-y-4">
              <div
                v-for="metric in subMetricsList"
                :key="'modal-' + metric.key"
                class="border border-gray-200 rounded-lg p-4"
              >
                <div class="flex items-baseline justify-between gap-3 mb-1.5">
                  <div class="flex items-center gap-2 min-w-0">
                    <div class="w-2 h-2 rounded-full flex-shrink-0" :class="getMetricColorClass(metric.normalized)" />
                    <h4
                      class="text-sm font-semibold truncate"
                      :class="metric.value != null ? 'text-gray-900' : 'text-gray-500'"
                    >
                      {{ metric.label }}
                    </h4>
                  </div>
                  <div class="flex items-baseline gap-3 flex-shrink-0">
                    <span v-if="metric.weight" class="text-[11px] text-gray-500 tabular-nums">
                      Weight {{ Math.round(metric.weight * 100) }}%
                    </span>
                    <span
                      class="text-lg font-bold tabular-nums"
                      :class="metric.value != null ? 'text-gray-900' : 'text-gray-400'"
                    >
                      {{ metric.display }}
                    </span>
                  </div>
                </div>
                <p
                  v-if="metric.description"
                  class="text-xs text-gray-600 leading-relaxed whitespace-pre-line"
                >
                  {{ metric.description }}
                </p>
                <p v-else class="text-xs text-gray-400 italic">
                  No description configured.
                </p>

                <div
                  v-if="metric.breakdown"
                  class="mt-4 bg-gray-50 rounded-md p-3 text-xs"
                >
                  <div class="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
                    Calculation
                  </div>

                  <!-- Tier -->
                  <div class="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 text-gray-700">
                    <span class="text-gray-500">Tier</span>
                    <span>
                      {{ metric.breakdown.tier }}
                      <span v-if="metric.breakdown.tier_inferred" class="text-gray-400 italic">(default — no tier tag set)</span>
                    </span>
                  </div>

                  <!-- Per-component table: PL and JOC each carry their own window -->
                  <div class="mt-3 pt-3 border-t border-gray-200 space-y-2">
                    <div class="grid grid-cols-[1fr_3rem_3.75rem_3rem] gap-x-3 text-[10px] uppercase tracking-wide text-gray-400">
                      <span></span>
                      <span class="text-right">Actual</span>
                      <span class="text-right">Expected</span>
                      <span class="text-right">Ratio</span>
                    </div>

                    <div class="grid grid-cols-[1fr_3rem_3.75rem_3rem] gap-x-3 items-start">
                      <div class="min-w-0">
                        <div class="text-gray-600">In-person meetings</div>
                        <div class="text-[10px] text-gray-400">
                          {{ formatBreakdownDate(metric.breakdown.pl_window_start) }} – {{ formatBreakdownDate(metric.breakdown.pl_window_end) }} · trailing {{ metric.breakdown.pl_window_days }} days
                        </div>
                      </div>
                      <span class="tabular-nums text-gray-700 text-right">{{ metric.breakdown.pl_actual }}</span>
                      <span class="tabular-nums text-gray-400 text-right">{{ metric.breakdown.pl_expected }}</span>
                      <span class="tabular-nums font-semibold text-right" :class="ratioTextClass(metric.breakdown.pl_ratio)">{{ metric.breakdown.pl_ratio.toFixed(2) }}</span>
                    </div>

                    <div class="grid grid-cols-[1fr_3rem_3.75rem_3rem] gap-x-3 items-start">
                      <div class="min-w-0">
                        <div class="text-gray-600">JOC meetings</div>
                        <div class="text-[10px] text-gray-400">
                          {{ formatBreakdownDate(metric.breakdown.joc_window_start) }} – {{ formatBreakdownDate(metric.breakdown.joc_window_end) }} · {{ metric.breakdown.joc_window_days }} days<span v-if="metric.breakdown.joc_window_ytd"> (year-to-date)</span>
                        </div>
                      </div>
                      <span class="tabular-nums text-gray-700 text-right">{{ metric.breakdown.joc_actual }}</span>
                      <span class="tabular-nums text-gray-400 text-right">{{ metric.breakdown.joc_expected }}</span>
                      <span class="tabular-nums font-semibold text-right" :class="ratioTextClass(metric.breakdown.joc_ratio)">{{ metric.breakdown.joc_ratio.toFixed(2) }}</span>
                    </div>
                  </div>

                  <!-- Score row -->
                  <div class="mt-3 pt-3 border-t border-gray-200 flex items-baseline justify-between gap-3">
                    <span class="text-gray-500">Score</span>
                    <span class="tabular-nums text-gray-400">
                      ({{ metric.breakdown.pl_ratio.toFixed(2) }} + {{ metric.breakdown.joc_ratio.toFixed(2) }}) / 2 =
                      <span class="text-sm font-bold ml-1" :class="ratioTextClass(metric.breakdown.score)">{{ metric.breakdown.score.toFixed(2) }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Modal footer -->
            <div
              v-if="missingCount > 0"
              class="px-6 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500"
            >
              {{ missingCount }} metric{{ missingCount > 1 ? 's' : '' }} not yet available for this practice.
            </div>
          </div>
        </div>
      </Teleport>
    </div>
    <span v-else class="text-gray-400 text-sm">--</span>
  </div>
</template>

<script>
export default {
  name: 'CC_Engagement_Score_Badge',
  props: {
    params: {
      type: Object,
      required: true
    },
    // When true, lay the sub-metric dots beside the composite badge instead
    // of below it. Useful in dense rows / tables. Defaults to false to keep
    // existing AG Grid cell usages visually unchanged.
    inline: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      tooltipVisible: false,
      tooltipStyle: {},
      modalOpen: false,
      closeTimer: null
    }
  },
  computed: {
    scoreData() {
      const val = this.params.value
      if (val == null) return null
      if (typeof val === 'object') return val
      return { composite: val, sub_scores: {} }
    },
    hasScore() {
      return this.scoreData?.composite != null
    },
    compositeScore() {
      return this.scoreData?.composite
    },
    roundedScore() {
      return this.compositeScore.toFixed(1)
    },
    subScores() {
      return this.scoreData?.sub_scores || {}
    },
    subMetricsList() {
      return Object.entries(this.subScores).map(([key, meta]) => {
        const isNewFormat = meta && typeof meta === 'object' && 'value' in meta
        const value = isNewFormat ? meta.value : meta
        const maxValue = isNewFormat ? meta.max : 1.0
        const name = isNewFormat ? meta.name : key
        const description = isNewFormat ? (meta.description || '') : ''
        const weight = isNewFormat ? (meta.weight || 0) : 0
        const breakdown = isNewFormat ? (meta.breakdown || null) : null
        const normalized = value != null && maxValue > 0 ? Math.min(value / maxValue, 1.0) : null
        return {
          key,
          value,
          label: name,
          description,
          weight,
          breakdown,
          display: value != null ? value.toFixed(1) : '--',
          normalized
        }
      })
    },
    missingCount() {
      return this.subMetricsList.filter(m => m.value == null).length
    },
    badgeClass() {
      const score = parseFloat(this.roundedScore)
      if (score >= 4.0) return 'border-green-500 text-green-700'
      if (score >= 3.0) return 'border-yellow-500 text-yellow-700'
      if (score >= 2.0) return 'border-primary-500 text-primary-700'
      return 'border-red-500 text-red-700'
    }
  },
  methods: {
    onBadgeEnter() {
      this.cancelClose()
      this.openTooltip()
    },
    onBadgeLeave() {
      this.scheduleClose()
    },
    onTooltipEnter() {
      this.cancelClose()
    },
    onTooltipLeave() {
      this.scheduleClose()
    },
    openTooltip() {
      const el = this.$refs.badge
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.tooltipStyle = {
        left: rect.left + rect.width / 2 + 'px',
        top: rect.top + 'px',
        transform: 'translate(-50%, -100%) translateY(-8px)'
      }
      this.tooltipVisible = true
    },
    scheduleClose() {
      this.cancelClose()
      this.closeTimer = setTimeout(() => {
        this.tooltipVisible = false
      }, 120)
    },
    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    openModal() {
      this.cancelClose()
      this.tooltipVisible = false
      this.modalOpen = true
    },
    closeModal() {
      this.modalOpen = false
    },
    getDotColorClass(normalized) {
      if (normalized == null) return 'bg-gray-300'
      if (normalized >= 0.8) return 'bg-green-500'
      if (normalized >= 0.6) return 'bg-yellow-400'
      if (normalized >= 0.4) return 'bg-orange-400'
      return 'bg-red-500'
    },
    getMetricColorClass(normalized) {
      if (normalized == null) return 'bg-gray-300'
      if (normalized >= 0.8) return 'bg-green-500'
      if (normalized >= 0.6) return 'bg-yellow-400'
      if (normalized >= 0.4) return 'bg-orange-400'
      return 'bg-red-500'
    },
    formatBreakdownDate(iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-').map(Number)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${months[m - 1]} ${d}`
    },
    ratioTextClass(ratio) {
      if (ratio == null) return 'text-gray-400'
      if (ratio >= 0.8) return 'text-green-700'
      if (ratio >= 0.6) return 'text-yellow-700'
      if (ratio >= 0.4) return 'text-orange-700'
      return 'text-red-700'
    }
  },
  beforeUnmount() {
    this.cancelClose()
  }
}
</script>
